import { FC } from 'react';

import { Box } from '@mui/material';

import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import useCountryHelpers from 'src/hooks/useCountryHelpers';

import AddressDisplay from '../AddressDisplay';
import { CardSection } from '../CardSection';
import { CountryDisplay } from '../CountryDisplay';
import { TextDisplay } from '../TextDisplay';

const MerchantInformation: FC = () => {
    const { merchantApplication } = useAddMerchantContext();
    const { getPostalLabel } = useCountryHelpers();
    const postalLabel = getPostalLabel(merchantApplication?.partner?.countryDetails?.alpha2Code);

    return (
        <Box display="flex" flexDirection="column" gap="24px">
            <CardSection heading="About Business">
                <Box display="flex" flexDirection="column" gap="24px">
                    <TextDisplay
                        label="Business Type"
                        value={
                            merchantApplication?.merchantInformation?.aboutBusiness?.businessType ??
                            ''
                        }
                    />
                    <Box width="100%" display="flex" justifyContent="space-between">
                        <TextDisplay
                            label="Legal Company Name"
                            value={
                                merchantApplication?.merchantInformation?.aboutBusiness
                                    ?.legalCompanyName ?? ''
                            }
                        />
                        <TextDisplay
                            label="Website"
                            value={
                                merchantApplication?.merchantInformation?.aboutBusiness?.website ??
                                ''
                            }
                        />
                    </Box>

                    <Box width="100%" display="flex" justifyContent="space-between">
                        <TextDisplay
                            label="Registered Number"
                            value={
                                merchantApplication?.merchantInformation?.aboutBusiness
                                    ?.registeredNumber ?? ''
                            }
                        />

                        <TextDisplay
                            label="Vat Registered"
                            value={
                                (merchantApplication?.merchantInformation?.aboutBusiness?.vat
                                    ?.vatRegistered ?? false)
                                    ? 'Yes'
                                    : 'No'
                            }
                        />
                    </Box>
                    <Box width="100%" display="flex" justifyContent="space-between">
                        <TextDisplay
                            label="Trading Name"
                            value={merchantApplication?.tradingName ?? ''}
                        />
                        {(merchantApplication?.merchantInformation?.aboutBusiness?.vat
                            ?.vatRegistered ??
                            false) && (
                            <TextDisplay
                                label="Vat Number"
                                value={
                                    merchantApplication?.merchantInformation?.aboutBusiness?.vat
                                        ?.vatNumber ?? ''
                                }
                            />
                        )}
                    </Box>
                    <TextDisplay
                        label="Number of Outlets"
                        value={merchantApplication?.merchantInformation?.numberOfOutlets ?? '1'}
                    />
                </Box>
            </CardSection>
            <CardSection heading="Address Information">
                <AddressDisplay
                    heading="Registered Address"
                    postalLabel={postalLabel}
                    eirCode={
                        merchantApplication?.merchantInformation?.aboutBusiness?.registeredAddress
                            ?.eircode ?? ''
                    }
                    addressLine1={
                        merchantApplication?.merchantInformation?.aboutBusiness?.registeredAddress
                            ?.addressLine1 ?? ''
                    }
                    addressLine2={
                        merchantApplication?.merchantInformation?.aboutBusiness?.registeredAddress
                            ?.addressLine2 ?? ''
                    }
                    city={
                        merchantApplication?.merchantInformation?.aboutBusiness?.registeredAddress
                            ?.townOrCity ?? ''
                    }
                    country={
                        merchantApplication?.merchantInformation?.aboutBusiness?.registeredAddress
                            ?.country ?? ''
                    }
                    county={
                        merchantApplication?.merchantInformation?.aboutBusiness?.registeredAddress
                            ?.county ?? ''
                    }
                />
                <Box marginBottom="24px" />
                <AddressDisplay
                    heading="Trading Address"
                    postalLabel={postalLabel}
                    eirCode={
                        merchantApplication?.merchantInformation?.outlets?.[0]?.tradingAddress
                            ?.eircode ?? ''
                    }
                    addressLine1={
                        merchantApplication?.merchantInformation?.outlets?.[0]?.tradingAddress
                            ?.addressLine1 ?? ''
                    }
                    addressLine2={
                        merchantApplication?.merchantInformation?.outlets?.[0]?.tradingAddress
                            ?.addressLine2 ?? ''
                    }
                    city={
                        merchantApplication?.merchantInformation?.outlets?.[0]?.tradingAddress
                            ?.townOrCity ?? ''
                    }
                    country={
                        merchantApplication?.merchantInformation?.outlets?.[0]?.tradingAddress
                            ?.country ?? ''
                    }
                    county={
                        merchantApplication?.merchantInformation?.outlets?.[0]?.tradingAddress
                            ?.county ?? ''
                    }
                />
            </CardSection>
            {(merchantApplication?.merchantInformation?.principals ?? []).map(
                (principal, index) => {
                    return (
                        <CardSection heading={`Principal ${index + 1}`} key={index}>
                            <Box display="flex" flexDirection="column" gap="24px">
                                <Box width="100%" display="flex" justifyContent="space-between">
                                    <TextDisplay label="First Name" value={principal?.firstName} />
                                    <TextDisplay
                                        label="Middle Name(s)"
                                        value={principal?.middleNames}
                                    />
                                </Box>
                                <Box width="100%" display="flex" justifyContent="space-between">
                                    <TextDisplay label="Last Name" value={principal?.lastName} />
                                    <TextDisplay
                                        label="Date of Birth"
                                        value={principal?.dateOfBirth}
                                    />
                                </Box>
                                <Box width="100%" display="flex" justifyContent="space-between">
                                    <TextDisplay
                                        label="Nationality"
                                        value={principal?.nationality}
                                    />
                                    <TextDisplay label="Email Address" value={principal?.email} />
                                </Box>
                                <Box width="100%" display="flex" justifyContent="space-between">
                                    <CountryDisplay
                                        label="Mobile Number"
                                        value={principal?.mobileNumber}
                                        country={
                                            merchantApplication?.merchantInformation
                                                ?.tradingAddress_country
                                        }
                                    />
                                    <CountryDisplay
                                        label="Telephone Number"
                                        value={principal?.telephoneNumber}
                                        country={
                                            merchantApplication?.merchantInformation
                                                ?.tradingAddress_country
                                        }
                                    />
                                </Box>
                                <Box width="100%" display="flex" justifyContent="space-between">
                                    <TextDisplay
                                        label="Percentage of Ownership"
                                        value={`${principal?.percentageOfOwnership}%`}
                                    />
                                    <TextDisplay label="Position" value={principal?.position} />
                                </Box>
                                {(principal?.addresses ?? []).map((address, addressIndex) => {
                                    return (
                                        <AddressDisplay
                                            key={`principal-${index}-addresses-${addressIndex}`}
                                            heading={
                                                address.type === 'PERSONAL_ADDRESS'
                                                    ? 'Personal Address'
                                                    : 'Previous Address'
                                            }
                                            postalLabel={postalLabel}
                                            eirCode={address?.eircode ?? ''}
                                            addressLine1={address?.addressLine1 ?? ''}
                                            addressLine2={address?.addressLine2 ?? ''}
                                            city={address?.city ?? ''}
                                            country={address?.country}
                                            county={address?.county ?? 'N/A'}
                                            timeAtAddress={address?.timeAtAddress}
                                        />
                                    );
                                })}
                            </Box>
                        </CardSection>
                    );
                }
            )}
        </Box>
    );
};

export default MerchantInformation;
